import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, ModalShell } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

/** Cuenta a la que las clínicas transfieren la suscripción, una por moneda de la factura. */
type TransferData = {
  titular: string;
  alias: string;
  cbu: string;
  banco: string;
  cuit: string;
  notas: string;
};

type TransferCurrency = 'ARS' | 'USD';

type TransferAccounts = Record<TransferCurrency, TransferData>;

const EMPTY_ACCOUNT: TransferData = { titular: '', alias: '', cbu: '', banco: '', cuit: '', notas: '' };

const EMPTY: TransferAccounts = { ARS: { ...EMPTY_ACCOUNT }, USD: { ...EMPTY_ACCOUNT } };

function readAccount(value: unknown): TransferData {
  const row = value && typeof value === 'object' ? (value as Partial<TransferData>) : {};
  return {
    titular: row.titular ?? '',
    alias: row.alias ?? '',
    cbu: row.cbu ?? '',
    banco: row.banco ?? '',
    cuit: row.cuit ?? '',
    notas: row.notas ?? '',
  };
}

/**
 * "Datos para transferir": una cuenta en pesos y otra en dólares. El dueño ve en Coofit → Suscripción
 * la cuenta de la moneda de su factura.
 */
export const TransferDataModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { error: showError, success } = useToast();
  const [data, setData] = useState<TransferAccounts>(EMPTY);
  const [currency, setCurrency] = useState<TransferCurrency>('ARS');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [missingMigration, setMissingMigration] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoading(true);
    setCurrency('ARS');
    void httpClient
      .get<{ disponible?: boolean; ARS?: Partial<TransferData>; USD?: Partial<TransferData> }>(
        '/panel-config/transferencia'
      )
      .then((res) => {
        if (cancelled) return;
        const d = res.data ?? {};
        setMissingMigration(d.disponible === false);
        setData({ ARS: readAccount(d.ARS), USD: readAccount(d.USD) });
      })
      .catch(() => {
        if (!cancelled) showError(t('pages.paymentNotices.transferLoadError'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, showError, t]);

  const account = data[currency];
  const set =
    (key: keyof TransferData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setData((prev) => ({ ...prev, [currency]: { ...prev[currency], [key]: e.target.value } }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await httpClient.put('/panel-config/transferencia', data);
      success(t('pages.paymentNotices.transferSaved'));
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showError(message || t('pages.paymentNotices.transferSaveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const disabled = isLoading || isSaving || missingMigration;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={() => (isSaving ? undefined : onClose())}
      title={t('pages.paymentNotices.transferTitle')}
      subtitle={t('pages.paymentNotices.transferSubtitle')}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void handleSave()} isLoading={isSaving} disabled={disabled}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 p-1">
        {missingMigration ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-100">
            {t('pages.paymentNotices.transferMissingMigration')}
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 dark:bg-gray-900/50">
          {(['ARS', 'USD'] as const).map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setCurrency(code)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
                currency === code
                  ? 'bg-white text-slate-900 shadow-sm dark:bg-gray-700 dark:text-white'
                  : 'text-slate-600 hover:text-slate-900 dark:text-gray-300 dark:hover:text-white'
              }`}
            >
              {t(code === 'ARS' ? 'pages.paymentNotices.transferCurrencyArs' : 'pages.paymentNotices.transferCurrencyUsd')}
            </button>
          ))}
        </div>
        <Input
          label={t('pages.paymentNotices.transferHolder')}
          placeholder={t('pages.paymentNotices.transferHolderPlaceholder')}
          value={account.titular}
          onChange={set('titular')}
          maxLength={150}
          disabled={disabled}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label={t('pages.paymentNotices.transferAlias')}
            placeholder={t('pages.paymentNotices.transferAliasPlaceholder')}
            value={account.alias}
            onChange={set('alias')}
            maxLength={60}
            disabled={disabled}
          />
          <Input
            label={t('pages.paymentNotices.transferCbu')}
            value={account.cbu}
            onChange={set('cbu')}
            maxLength={40}
            inputMode="numeric"
            disabled={disabled}
          />
          <Input
            label={t('pages.paymentNotices.transferBank')}
            placeholder={t('pages.paymentNotices.transferBankPlaceholder')}
            value={account.banco}
            onChange={set('banco')}
            maxLength={100}
            disabled={disabled}
          />
          <Input
            label={t('pages.paymentNotices.transferCuit')}
            value={account.cuit}
            onChange={set('cuit')}
            maxLength={20}
            disabled={disabled}
          />
        </div>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('pages.paymentNotices.transferNotes')}
          </span>
          <textarea
            value={account.notas}
            onChange={set('notas')}
            maxLength={500}
            rows={2}
            disabled={disabled}
            placeholder={t('pages.paymentNotices.transferNotesPlaceholder')}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
        </label>
        <p className="text-xs text-slate-500">{t('pages.paymentNotices.transferEmpty')}</p>
      </div>
    </ModalShell>
  );
};
